import { useState } from "react";

/**
 * Public "we want to buy your car" funnel, rendered by FunnelPage at /r/:token
 * when the recipient belongs to a vehicle_acquisition campaign.
 *
 * Step 1: confirm the vehicle (or tell us what it is) + mileage + condition.
 * Step 2: an estimate RANGE, or an in-person appraisal offer.
 * Step 3: accepted — the store is notified.
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO
 * ----------------------------------------
 * 1. It never shows a single number. The server returns a low/high band, and the
 *    copy says plainly that it's an estimate subject to inspection. Our trade
 *    valuation is not yet calibrated well enough to quote flat, and a number a
 *    customer anchors on is one we have to honor or walk back at the appraisal.
 * 2. It never treats a missing estimate as an error. `hasEstimate: false` is a
 *    normal outcome — the server suppresses the range whenever the valuation
 *    engine signals it's extrapolating — and the page carries on to the same
 *    appointment CTA. The goal is an appraisal, which we reach either way.
 *
 * Mileage is required and always typed by the customer: no odometer reading
 * exists in any system we can read, so this form is the only mileage we get.
 */

const CONDITIONS = [
  { value: "outstanding", label: "Outstanding", hint: "Like new, no flaws" },
  { value: "clean", label: "Clean", hint: "Minor wear, well maintained" },
  { value: "average", label: "Average", hint: "Normal wear for its age" },
  { value: "rough", label: "Rough", hint: "Needs cosmetic or mechanical work" },
] as const;

type Condition = (typeof CONDITIONS)[number]["value"];

export interface AcquisitionState {
  kind: "acquisition";
  state: "open" | "valued" | "accepted" | "expired";
  firstName?: string | null;
  dealerName?: string;
  vehicleKnown?: boolean;
  vehicle?: string | null;
  vehicleYear?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  reportedMileage?: number | null;
  reportedCondition?: string | null;
  estimateLow?: string | number | null;
  estimateHigh?: string | number | null;
}

interface ValueResult {
  hasEstimate: boolean;
  estimateLow: number | null;
  estimateHigh: number | null;
  vehicle: string | null;
}

const money = (v: number | string | null | undefined): string | null => {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? `$${Math.round(n).toLocaleString("en-US")}` : null;
};

export function AcquisitionFunnel({
  token,
  initial,
}: {
  token: string | undefined;
  initial: AcquisitionState;
}) {
  const [step, setStep] = useState<"form" | "result" | "accepted">(
    initial.state === "accepted" ? "accepted" : initial.state === "valued" ? "result" : "form"
  );
  const [mileage, setMileage] = useState(
    initial.reportedMileage != null ? String(initial.reportedMileage) : ""
  );
  const [condition, setCondition] = useState<Condition | null>(
    (initial.reportedCondition as Condition | null) ?? null
  );
  const [year, setYear] = useState(initial.vehicleYear ? String(initial.vehicleYear) : "");
  const [make, setMake] = useState(initial.vehicleMake ?? "");
  const [model, setModel] = useState(initial.vehicleModel ?? "");
  const [result, setResult] = useState<ValueResult | null>(
    initial.state === "valued"
      ? {
          hasEstimate: initial.estimateLow != null && initial.estimateHigh != null,
          estimateLow: initial.estimateLow != null ? Number(initial.estimateLow) : null,
          estimateHigh: initial.estimateHigh != null ? Number(initial.estimateHigh) : null,
          vehicle: initial.vehicle ?? null,
        }
      : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dealer = initial.dealerName ?? "our dealership";
  const hello = initial.firstName ? `Hi ${initial.firstName}!` : "Hi there!";
  const knownVehicle = initial.vehicleKnown ? initial.vehicle : null;

  const mileageNum = Number(mileage.replace(/[^\d]/g, ""));
  const mileageValid = Number.isInteger(mileageNum) && mileageNum >= 0 && mileageNum <= 600_000;
  const vehicleValid = knownVehicle
    ? true
    : !!(year.trim() && make.trim() && model.trim() && Number(year) >= 1981 && Number(year) <= 2027);
  const canSubmit = mileageValid && !!condition && vehicleValid && !submitting;

  async function submitVehicle() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/funnel/${token}/vehicle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mileage: mileageNum,
          condition,
          ...(knownVehicle
            ? {}
            : { year: Number(year), make: make.trim(), model: model.trim() }),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setResult((await res.json()) as ValueResult);
      setStep("result");
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  }

  async function accept() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await fetch(`/api/public/funnel/${token}/accept`, { method: "POST" });
      setStep("accepted");
    } catch {
      // Don't strand them on a failed POST — the store still has their details
      // from the outreach record, so showing the confirmation is honest enough.
      setStep("accepted");
    }
    setSubmitting(false);
  }

  const lo = money(result?.estimateLow);
  const hi = money(result?.estimateHigh);

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900">{dealer}</h1>
        </div>

        {step === "form" && (
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <p className="text-lg font-medium text-slate-900">{hello}</p>
            <p className="mt-1 text-slate-600">
              {knownVehicle ? (
                <>
                  We're looking for clean, well-maintained vehicles right now — and your{" "}
                  <strong className="text-slate-900">{knownVehicle}</strong> is exactly the kind we
                  want. Tell us a couple of details and we'll show you what it's worth.
                </>
              ) : (
                <>
                  We're buying used vehicles right now, and we'd like to make you an offer on
                  yours. Tell us what you're driving and we'll show you what it's worth.
                </>
              )}
            </p>

            {!knownVehicle && (
              <div className="mt-5 grid grid-cols-3 gap-2">
                <input
                  inputMode="numeric"
                  placeholder="Year"
                  value={year}
                  onChange={(e) => setYear(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
                <input
                  placeholder="Make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
                <input
                  placeholder="Model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
              </div>
            )}

            <label className="mt-5 block text-sm font-medium text-slate-700">
              Current mileage
              <input
                inputMode="numeric"
                placeholder="e.g. 48,000"
                value={mileage}
                onChange={(e) => setMileage(e.target.value.replace(/[^\d,]/g, ""))}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
              />
            </label>

            <p className="mt-5 text-sm font-medium text-slate-700">Condition</p>
            <div className="mt-2 flex flex-col gap-2">
              {CONDITIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCondition(c.value)}
                  className={`rounded-lg border px-4 py-3 text-left text-sm transition ${
                    condition === c.value
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <span className="font-medium">{c.label}</span>
                  <span
                    className={`ml-2 text-xs ${
                      condition === c.value ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {c.hint}
                  </span>
                </button>
              ))}
            </div>

            {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}

            <button
              type="button"
              disabled={!canSubmit}
              onClick={submitVehicle}
              className="mt-6 w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
            >
              {submitting ? "Checking…" : "See what it's worth"}
            </button>
          </div>
        )}

        {step === "result" && (
          <div className="rounded-xl bg-white p-6 shadow-sm">
            {result?.hasEstimate && lo && hi ? (
              <>
                <p className="text-slate-600">
                  Based on {result.vehicle ?? "your vehicle"} at{" "}
                  {mileageNum.toLocaleString("en-US")} miles, we'd expect to offer around
                </p>
                <p className="mt-3 text-center text-3xl font-semibold text-slate-900">
                  {lo} – {hi}
                </p>
                <p className="mt-3 text-sm text-slate-500">
                  This is an estimate based on current market data, not a final offer. We'll
                  confirm the exact number after a quick look at the vehicle — it takes about 20
                  minutes and there's no obligation to sell.
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-medium text-slate-900">
                  We're interested in {result?.vehicle ?? "your vehicle"}.
                </p>
                <p className="mt-2 text-slate-600">
                  We'd rather give you a real number than a rough guess on this one. Bring it by
                  and we'll appraise it properly — about 20 minutes, no obligation to sell.
                </p>
              </>
            )}

            <button
              type="button"
              disabled={submitting}
              onClick={accept}
              className="mt-6 w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
            >
              {submitting ? "Sending…" : "Yes — I'm interested"}
            </button>
            <p className="mt-3 text-center text-xs text-slate-400">
              We'll call you to set up a time that works.
            </p>
          </div>
        )}

        {step === "accepted" && (
          <div className="rounded-xl bg-white p-6 text-center shadow-sm">
            <p className="text-lg font-medium text-slate-900">Thanks — we'll be in touch.</p>
            <p className="mt-2 text-slate-600">
              Someone from {dealer} will reach out shortly to arrange a time. Bring the vehicle,
              your ID and the title or loan details if you have them handy.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
